import { Grid } from "@mui/material";
import MDBox from "components/MDBox";
import UiFieldGroup from "components/base/UiFieldGroup";
import UiSideModal from "components/base/UiSideModal";
import UiTypography from "components/base/UiTypography";
import UiButton from "components/base/UiButton";
import { ProposalProduct } from "generated/graphql";
import productAttributesNames from "utils/products/productAttributes";
import { useFormContext } from "react-hook-form";
import { UpdateProposalProductInputSchema } from "DDD/action-objects/StageProductUpdate";
import { capitalCase } from "change-case";
import { useEffect, useRef } from "react";

type MaterialConfigModalProps = {
  open: boolean;
  onClose: () => void;
  product: ProposalProduct;
  onSave?: () => void;
};

export default function MaterialConfigModal({
  open,
  onClose,
  product,
  onSave,
}: MaterialConfigModalProps) {
  const { setValue, getValues } = useFormContext<UpdateProposalProductInputSchema>();
  const formAttributes = getValues("material.attributes") ?? {};

  const attributeKeys = Object.keys(formAttributes).filter((key) => key !== "__typename");

  const pendingEdits = useRef<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      pendingEdits.current = {};
    }
  }, [open]);

  const handleSave = async () => {
    for (const [key, value] of Object.entries(pendingEdits.current)) {
      setValue(`material.attributes.${key}` as any, value, { shouldDirty: true });
    }
    await onSave?.();
    onClose();
  };

  return (
    <UiSideModal open={open} onClose={onClose} title="Material Configuration" width="498px">
      <MDBox p="24px" display="flex" flexDirection="column" gap="12px" flex={1}>
        {attributeKeys.length > 0 && (
          <>
            <UiTypography uiVariant="heading-sm">Product Attributes</UiTypography>
            <Grid container spacing="8px">
              {attributeKeys.map((attributeKey) => {
                const attrMeta = productAttributesNames[attributeKey];
                const label = attrMeta?.label ?? capitalCase(attributeKey);

                return (
                  <Grid item xs={6} key={attributeKey}>
                    <UiFieldGroup
                      label={label}
                      value={String(
                        (formAttributes as Record<string, unknown>)[attributeKey] ?? ""
                      )}
                      placeholder={String(product.material.attributes?.[attributeKey] ?? "")}
                      inputProps={{
                        defaultValue: String(
                          (formAttributes as Record<string, unknown>)[attributeKey] ?? ""
                        ),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                          pendingEdits.current[attributeKey] = e.target.value;
                        },
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {attributeKeys.length === 0 && (
          <UiTypography uiVariant="body-sm" sx={{ color: "#7B809A" }}>
            No configurable attributes for this material.
          </UiTypography>
        )}
      </MDBox>

      <MDBox px="24px" py="12px" display="flex" justifyContent="flex-end">
        <UiButton uiVariant="primary-green" onClick={handleSave}>
          Save
        </UiButton>
      </MDBox>
    </UiSideModal>
  );
}
